import React, { useEffect, useState, useRef } from "react";
import { Button } from "../ui/button";
import { RotateCcwIcon, PlusIcon } from "lucide-react";
import { SemanticChart, ChartData } from "./SemanticChart";
import { SemanticService } from "../../services/SemanticService";
import AddSemanticLayerModal from "./modals/AddSemanticLayerModal";

// Define the structure for chart layer data
interface ChartLayerData {
  entityType: string;
  entityTypeId: string | number;
  name?: string;
}

interface MultiLayerSemanticChartProps {
  chartData: ChartLayerData[];
  apiKey: string;
}

export const MultiLayerSemanticChart: React.FC<MultiLayerSemanticChartProps> = ({
  chartData: initialChartData,
  apiKey
}) => {
  // State for the chart data and UI
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for tracking layers
  const [chartLayers, setChartLayers] = useState<(ChartData & { layerName?: string })[]>([]);
  
  // Convert layerConfigs from state to ref
  const layerConfigsRef = useRef<ChartLayerData[]>(initialChartData);
  
  // State for the original chart data (for reset functionality)
  const [originalChartData, setOriginalChartData] = useState<ChartData | null>(null);
  const [originalLayerConfigs, setOriginalLayerConfigs] = useState<ChartLayerData[]>(initialChartData);
  
  // State for the modal
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  
  // Use a ref to track if we've already fetched data
  const fetchedRef = useRef<boolean>(false);

  // Fetch the chart when the component mounts
  useEffect(() => {
    if (!fetchedRef.current && layerConfigsRef.current.length > 0) {
      fetchChart();
      fetchedRef.current = true;
    }
  }, []);

  // Fetch the chart with the current layer configurations
  const fetchChart = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare the payload for the semantic combo chart
      const chartDetailsList = layerConfigsRef.current.map(layer => ({
        entity_type: layer.entityType,
        entity_id: layer.entityTypeId
      }));
      
      const payload = {
        chart_details: chartDetailsList
      };
      
      // Call the SemanticService to build the combo chart
      SemanticService.buildSemanticComboChart(
        payload,
        setLoading,
        apiKey
      ).subscribe(
        (data: ChartData) => {
          // If this is the first fetch, store the original data
          if (!originalChartData) {
            setOriginalChartData(data);
            setOriginalLayerConfigs([...layerConfigsRef.current]);
          }
          
          // Update the chart data
          setChartData(data);
          
          // Create a layer name based on the entity type and position
          const isNewLayer = chartLayers.length > 0;
          const layerIndex = isNewLayer ? chartLayers.length : 0;
          const currentLayerConfig = layerConfigsRef.current[layerIndex] || layerConfigsRef.current[0];
          const entityType = currentLayerConfig.entityType.charAt(0).toUpperCase() + currentLayerConfig.entityType.slice(1);
          const layerName = isNewLayer 
            ? `Layer ${layerIndex + 1}: ${entityType} ${currentLayerConfig.name || currentLayerConfig.entityTypeId}`
            : `${entityType} ${currentLayerConfig.name || currentLayerConfig.entityTypeId}`;
          
          // Create a new layer with the data and name
          const newLayer = { ...data, layerName };
          
          // If we're fetching the initial chart, set it as the first layer
          if (chartLayers.length === 0) {
            setChartLayers([newLayer]);
          } else {
            // Otherwise, add it to the existing layers
            setChartLayers(prevLayers => [...prevLayers, newLayer]);
          }
          
          setLoading(false);
        },
        (error: any) => {
          console.error("Error fetching semantic chart:", error);
          setError(error.message || "Failed to load semantic chart");
          setLoading(false);
        }
      );
    } catch (err) {
      console.error("Error in semantic chart component:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
      setLoading(false);
    }
  };

  // Handle adding a new layer
  const handleAddLayer = (layerData: { entityType: string; entityTypeId: number }) => {
    // Create a new layer configuration
    const newLayerConfig: ChartLayerData = {
      entityType: layerData.entityType,
      entityTypeId: layerData.entityTypeId
    };

    
    // Update the layer configurations ref directly
    layerConfigsRef.current = [...layerConfigsRef.current, newLayerConfig];
    
    // Fetch the chart with the new layer
    fetchedRef.current = false;
    fetchChart();
  };

  // Handle resetting the chart to the original data
  const handleReset = () => {
    if (originalChartData) {
      setChartData(originalChartData);
      layerConfigsRef.current = originalLayerConfigs;
      const entityType = originalLayerConfigs[0].entityType.charAt(0).toUpperCase() + originalLayerConfigs[0].entityType.slice(1);
      const layerName = `${entityType} ${originalLayerConfigs[0].name || originalLayerConfigs[0].entityTypeId}`;
      setChartLayers([{ ...originalChartData, layerName }]);
    }
  };
  
  // Determine the entity ID and type for the modal
  const getEntityIdAndType = () => {
    if (layerConfigsRef.current.length === 0) return { datasetId: undefined, testsetId: undefined };
    
    const firstLayer = layerConfigsRef.current[0];
    if (firstLayer.entityType === "dataset") {
      return { datasetId: Number(firstLayer.entityTypeId), testsetId: undefined as number | undefined };
    } else if (firstLayer.entityType === "test_set") {
      return { datasetId: undefined as number | undefined, testsetId: Number(firstLayer.entityTypeId) };
    }
    
    return { datasetId: undefined as number | undefined, testsetId: undefined as number | undefined };
  };
  
  const { datasetId, testsetId } = getEntityIdAndType();
  
  return (
    <div className="space-y-4">
      {/* Chart Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-gc-med text-foreground">Semantic Distribution</h3>
        
        <div className="flex items-center gap-2">
          {chartLayers.length > 1 && (
            <Button 
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="gap-1"
              title="Reset to original layer"
            >
              <RotateCcwIcon className="w-3 h-3" />
              Reset
            </Button>
          )}
          
          <Button 
            onClick={() => setIsModalVisible(true)}
            disabled={loading}
            size="sm"
            className="gap-1"
          >
            <PlusIcon className="w-3 h-3" />
            Add Layer
          </Button>
        </div>
      </div>

      {/* Chart Content */}
      <SemanticChart
        data={chartData}
        loading={loading}
        error={error}
      />
      
      {/* Add Layer Modal */}
      <AddSemanticLayerModal
        isVisible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onAddLayer={handleAddLayer}
        datasetId={datasetId}
        testsetId={testsetId}
        apiKey={apiKey}
      />
    </div>
  );
};

export default MultiLayerSemanticChart; 